import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Button from '../Button/Button';

import styles from './UpgradeSubscription.module.scss';
import { useTheme } from '../../../../../ThemeContext';
import { useCheckoutStore } from '@jwp/ott-common/src/stores/CheckoutStore';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { formatAmount, formatLocalizedDate } from '@jwp/ott-common/src/utils/formatting';

type Props = {
  type: 'error' | 'success' | 'pending';
  onCloseButtonClick: () => void;
};

const UpgradeSubscription: React.FC<Props> = ({ type, onCloseButtonClick }: Props) => {
  const { t, i18n } = useTranslation('account');
  const upgradeDetails = useCheckoutStore.getState().upgradeSelectedOffer;
  const activeSubscription = useAccountStore((s) => s.subscription);
  const { universalStyles } = useTheme();
  const handleDescription = (type: string | undefined) => {
    const plan = <b className={styles.values}>{upgradeDetails?.title} Plan</b>;
    const price = (
      <b className={styles.values}>
        {upgradeDetails?.nextPaymentPriceCurrencySymbol}
        {formatAmount(upgradeDetails?.nextPaymentPrice ?? 0)}
      </b>
    );
    const date = <b className={styles.values}>{activeSubscription && formatLocalizedDate(new Date(activeSubscription?.expiresAt * 1000), i18n.language)}</b>;

    const baseStyle = { color: universalStyles.carouselPaginationInactive.backgroundColor };

    switch (type) {
      case 'upgrade':
        return (
          <p style={baseStyle}>
            <Trans
              i18nKey="checkout.upgrade_success_message"
              ns="account"
              values={{
                plan: `${upgradeDetails?.title} Plan`,
                price: `${upgradeDetails?.nextPaymentPriceCurrencySymbol}${formatAmount(upgradeDetails?.nextPaymentPrice ?? 0)}`,
                date: activeSubscription
                  ? formatLocalizedDate(new Date(activeSubscription?.expiresAt * 1000), i18n.language)
                  : '',
              }}
              components={{
                1: <b className={styles.values} />,
                3: <b className={styles.values} />,
                5: <b className={styles.values} />,
              }}
            />
          </p>
        );

      case 'downgrade':
        return (
          <p style={baseStyle}>
            <Trans
              i18nKey="checkout.downgrade_success_message"
              ns="account"
              values={{
                plan: `${upgradeDetails?.title} Plan`,
                price: `${upgradeDetails?.nextPaymentPriceCurrencySymbol}${formatAmount(upgradeDetails?.nextPaymentPrice ?? 0)}`,
                date: activeSubscription
                  ? formatLocalizedDate(new Date(activeSubscription?.expiresAt * 1000), i18n.language)
                  : '',
              }}
              components={{
                1: <b className={styles.values} />,
                3: <b className={styles.values} />,
                5: <b className={styles.values} />,
              }}
            />
          </p>
        );

      default:
        return '';
    }
  };

  useEffect(() => {
    return () => {
      useCheckoutStore.setState({ upgradeSelectedOffer: null });
    };
  }, []);

  return (
    <div className={styles.container}>
      <svg width="58" height="58" viewBox="0 0 58 58" fill="none" xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="29.0014" cy="28.9881" rx="25.7045" ry="25.6927" fill="#E32652" />
        <ellipse cx="29" cy="28.9866" rx="29" ry="28.9866" fill="#22C55E" />
        <path d="M17 30.186L24.1588 37.2843C24.5486 37.6708 25.1772 37.6708 25.567 37.2843L42 20.9902" stroke="white" strokeWidth="4" strokeLinecap="round" />
      </svg>
      <h2 className={styles.title} style={universalStyles.primaryTextColor}>
        {t(`checkout.${upgradeDetails?.switchDirection}`)}
      </h2>
      <p className={styles.message}>{handleDescription(upgradeDetails?.switchDirection)}</p>
      <div style={{ width: '100%' }}>
        <Button label={t('checkout.okay')} onClick={onCloseButtonClick} color="primary" variant="contained" size="large" fullWidth />
      </div>
    </div>
  );
};

export default UpgradeSubscription;
